import { SyncFolderService } from './sync-folder';
import { persistRestoredFolderProject } from './sync-folder-restore-persistence';
import { broadcastProjectsChanged } from './project-changes-channel';
import { getAllProjectSummaries } from './database';

export interface RestoreProjectResult {
    success: boolean;
    project: any;
    diagnostics: any;
    message: string;
}

export interface RestoreProjectDependencies {
    setProjects?: (projects: any[]) => void;
    setToast?: (toast: { message: string; type: 'warning' | 'success' | 'error' | 'info' }) => void;
}

export async function restoreProjectFromDirectoryHandle(
    directoryHandle: any,
    folderMetadata: any = {},
    deps: RestoreProjectDependencies = {}
): Promise<RestoreProjectResult> {
    const { setProjects, setToast } = deps;
    try {
        const restored = await SyncFolderService.restoreProjectFromFolder(directoryHandle, folderMetadata);
        if (!restored?.project) {
            throw new Error('QuoteData.json could not be read from that project folder.');
        }

        const persisted = await persistRestoredFolderProject(restored);
        const { project } = persisted;
        broadcastProjectsChanged({ kind: 'restore', projectIds: [project.id] });

        try {
            const refreshed = await getAllProjectSummaries();
            if (Array.isArray(refreshed) && setProjects) setProjects(refreshed);
        } catch (err: any) {
            console.warn('[SyncFolder] Project restored, but history refresh failed:', err?.message);
        }

        const diagnostics = {
            ...((restored as any).diagnostics || {}),
            warnings: [
                ...((restored as any).diagnostics?.warnings || []),
            ],
        };
        const warningCount = Array.isArray(diagnostics.warnings) ? diagnostics.warnings.length : 0;
        const message = [
            `Restored "${project.title}" at Stage ${project.currentStage}.`,
            `${diagnostics.restoredPhotos ?? project.photos.length} photos`,
            `${diagnostics.restoredDocuments ?? project.documents.length} documents`,
            warningCount > 0 ? `${warningCount} recovery warning${warningCount === 1 ? '' : 's'}` : null,
        ].filter(Boolean).join(' ');
        if (setToast) {
            setToast({ message, type: warningCount > 0 ? 'warning' : 'success' });
        }

        return {
            success: true,
            project,
            diagnostics,
            message,
        };
    } catch (err: any) {
        const message = `Project restore failed: ${err?.message || 'unknown error'}`;
        if (setToast) {
            setToast({ message, type: 'error' });
        }
        throw new Error(message);
    }
}
